import { useState } from 'react';
import numeral from 'numeral';

// Constellation
import {
  AdornedInput,
  Adornment,
  Button,
  ButtonGroup,
  Heading,
  Input,
  Label,
  ModalDialog,
  Paragraph,
  Spacer,
  TextButton,
} from '@zillow/constellation';

import { useGetUser } from '../../hooks/queries/user';
import { useGetAddress } from '../../hooks/queries/address';
import { useGetFilter } from '../../hooks/queries/filter';

import { updateFilterAndNavigate } from '../../flux/actions/FilterActions';

import pathsManifest from '../../utils/pathsManifest';
// @ts-ignore
import { augmentError } from '../../utils/errors';

// Misc / utils
import { StyledInputError } from './StyledComponents';

// const logger = getLogger('components/ChangePriceModal');

const NoZestimateModal = ({ isOpen, onClose, address }: { isOpen: boolean; onClose: () => void; address: string }) => {
  const [inputPrice, updateInputPrice] = useState('');
  const [errorMsg, updateErrorMsg] = useState('');

  const { data: user } = useGetUser();
  const { data: queryAddress } = useGetAddress();
  const { data: filter, updateFilter } = useGetFilter();

  const handleCancel = () => {
    updateErrorMsg('');
    onClose();
  };

  const handlePriceChange = (e: React.FormEvent<HTMLInputElement>) => {
    updateErrorMsg('');

    // @ts-ignore https://github.com/desktop/desktop/issues/297
    const enteredValue = e.target.value;

    updateInputPrice(enteredValue ? numeral(enteredValue).format('0,0') : '');
  };

  const handleSubmit = async () => {
    if (!queryAddress || !filter) {
      return null;
    }

    const price = numeral(inputPrice).value();
    if (price === null) {
      updateErrorMsg('Please enter an amount');
      return;
    }

    const partialFilterObj = { price };

    await updateFilterAndNavigate(
      `${pathsManifest.PriceMyRentalPageResults}`,
      partialFilterObj,
      user,
      queryAddress,
      updateFilter,
      filter,
    )().catch(
      // eslint-disable-next-line
      (err: any) => {
        updateErrorMsg('There has been an error. Please try again.');
        augmentError(err, {
          root: 'components/NoZestimateModal#handleSubmit',
        });

        // logger.error(formatForLog(err), 'Filter: Error handling filter price change');
      },
    );
  };

  return (
    <ModalDialog
      mediaQuery={{ xs: { fullScreen: true }, lg: { fullScreen: false } }}
      onClose={handleCancel}
      header={
        <Heading level={5}>
          Rent Zestimate<sup>®</sup> not found
        </Heading>
      }
      isOpen={isOpen}
      renderBody={
        <div>
          <Paragraph marginTop="sm" marginBottom="xs">
            We’re unable to provide a Rent Zestimate<sup>®</sup> for <strong>{`${address}`}</strong>.
          </Paragraph>

          <Paragraph marginBottom="sm">
            Enter a custom price and we&#39;ll show you nearby rental listings closest to the price you choose.
          </Paragraph>

          <Label required>Custom price</Label>

          <AdornedInput
            error={errorMsg !== ''}
            input={<Input required onChange={handlePriceChange} placeholder="Enter amount" value={inputPrice} />}
            leftAdornment={<Adornment>$</Adornment>}
            marginTop="xs"
            // @ts-ignore
            size="lg"
          />

          {errorMsg !== '' && <StyledInputError>{errorMsg}</StyledInputError>}

          <Spacer marginTop="xs" marginBottom="sm" />
        </div>
      }
      renderFooter={
        <ButtonGroup aria-label="See listings">
          <TextButton onClick={handleCancel}>Cancel</TextButton>
          <Button buttonType="primary" onClick={handleSubmit}>
            See listings
          </Button>
        </ButtonGroup>
      }
    />
  );
};

export default NoZestimateModal;
